// Invoices In and Out, the last seven days (owner, 2026-11-04 — D-461: "Maybe
// Invoices In / Invoices Out over the last seven days is something"). One pair
// of columns per day, oldest on the left: In is invoices uploaded that day, Out
// is invoices exported to Point Click Care that day (the Export Control click,
// D-440).
//
// SINCE 2026-11-04 THE DASHBOARD DRAWS ONE PAIR PER PAYMENT CYCLE (owner —
// D-469: "Exports happen on Tuesdays, so we do not need to show an export every
// day"): a column is a Wednesday-to-Tuesday cycle, `date` its Wednesday. It
// sits under the cycle's own tiles, so a plain key names the two colours and
// there is no totals row (the totals and net-change row went with the unchosen
// Dashboard previews, 2026-11-04).
//
// Out counts exports only. An invoice archived from Denial Review also leaves
// the queue, and is not counted here.
import { Caption1, makeStyles, tokens } from '@fluentui/react-components'
import { SECTION_COLORS } from '../lib/theme'
import type { InOutDay } from '../lib/dashboardFigures'

export type { InOutDay } from '../lib/dashboardFigures'

const IN_COLOR = SECTION_COLORS.step
const OUT_COLOR = SECTION_COLORS.spend
const PLOT_HEIGHT = 120

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '12px' },
  legend: { display: 'flex', gap: '28px', alignItems: 'baseline', flexWrap: 'wrap' },
  sub: { color: tokens.colorNeutralForeground3 },
  key: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  swatch: { width: '10px', height: '10px', borderRadius: '2px', flexShrink: 0 },
  plot: {
    display: 'grid',
    gap: '8px',
    alignItems: 'end',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  day: { display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '4px' },
  col: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', width: '22px' },
  bar: { width: '100%', borderRadius: '3px 3px 0 0', minHeight: '2px' },
  value: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground2 },
  labels: { display: 'grid', gap: '8px', justifyItems: 'center' },
})

export function InOutChart({ days }: { days: readonly InOutDay[] }) {
  const s = useStyles()
  // One column per day, however many days the caller shows.
  const columns = `repeat(${days.length}, minmax(0, 1fr))`
  const max = Math.max(1, ...days.map((d) => Math.max(d.in, d.out)))
  const height = (n: number) => `${Math.round((n / max) * PLOT_HEIGHT)}px`

  const name = (d: InOutDay) => d.title ?? d.label

  return (
    <div className={s.root}>
      <div className={s.legend}>
        <span className={s.key}>
          <span className={s.swatch} style={{ backgroundColor: IN_COLOR }} aria-hidden />
          <Caption1 className={s.sub}>In (Uploaded)</Caption1>
        </span>
        <span className={s.key}>
          <span className={s.swatch} style={{ backgroundColor: OUT_COLOR }} aria-hidden />
          <Caption1 className={s.sub}>Out (Exported)</Caption1>
        </span>
      </div>
      <div className={s.plot} style={{ height: `${PLOT_HEIGHT + 18}px`, gridTemplateColumns: columns }} role="img" aria-label={days.map((d) => `${name(d)}: ${d.in} in, ${d.out} out`).join('; ')}>
        {days.map((d) => (
          <div key={d.date} className={s.day}>
            <div className={s.col} title={`${name(d)}: ${d.in} uploaded`}>
              <span className={s.value}>{d.in}</span>
              <div className={s.bar} style={{ height: height(d.in), backgroundColor: IN_COLOR }} />
            </div>
            <div className={s.col} title={`${name(d)}: ${d.out} exported`}>
              <span className={s.value}>{d.out}</span>
              <div className={s.bar} style={{ height: height(d.out), backgroundColor: OUT_COLOR }} />
            </div>
          </div>
        ))}
      </div>
      <div className={s.labels} style={{ gridTemplateColumns: columns }}>
        {days.map((d) => (
          <Caption1 key={d.date} className={s.sub}>
            {d.label}
          </Caption1>
        ))}
      </div>
    </div>
  )
}
